import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { publicRequest, userRequest } from "../api/requestMethods";
import {
  MAIN,
  applyBranchEventToSections,
  branchEventIsFor,
} from "../utils/branchAvailability";

// Async thunks
export const fetchBrands = createAsyncThunk(
  "brands/fetchBrands",
  async (_, { rejectWithValue }) => {
    try {
      const response = await publicRequest.get("/api/brands");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch brands"
      );
    }
  }
);

export const fetchFeaturedBrands = createAsyncThunk(
  "brands/fetchFeaturedBrands",
  async (_, { rejectWithValue }) => {
    try {
      const response = await publicRequest.get("/api/brands?featured=true");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch featured brands"
      );
    }
  }
);

export const fetchFeaturedBrandsToggle = createAsyncThunk(
  "brands/fetchFeaturedBrandsToggle",
  async (_, { rejectWithValue }) => {
    try {
      const response = await publicRequest.get("/api/brands/settings/featured-brands");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch featured brands toggle"
      );
    }
  }
);

/* The brand and the location its storefront is open on: a bare id (the main
   location) or { brandId, outletId }. Shared by both thunks below. */
const brandArg = (arg) =>
  arg && typeof arg === "object"
    ? { brandId: arg.brandId, outletId: arg.outletId || null }
    : { brandId: arg, outletId: null };

// One brand row, shared by the storefront's load and the quiet refresh below.
// The read always names the location (4 Oct 2026): every product comes back
// as it stands there (on sale, sold out or switched off there, the price
// there), and naming "main" shows a signed-in owner or admin what a shopper
// sees rather than the head office editor's view.
const getBrandRow = async (arg, { rejectWithValue, getState }) => {
  const { brandId, outletId } = brandArg(arg);
  try {
    const u = getState().user?.currentUser;
    const token = u?.token || u?.accessToken;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await userRequest.get(`/api/brands/${brandId}`, {
      headers,
      params: { outletId: outletId || MAIN },
    });
    return response.data.data;
  } catch (error) {
    // The status rides along so the storefront can tell a brand that does
    // not exist (404: say so, and tell search engines not to index the
    // address) from a read that merely failed (no answer, 5xx: offer a
    // retry, and never mark the page noindex). Google renders the page
    // with JavaScript; on 18 Oct 2026 the API did not answer during one such
    // render, the page declared SWT Rice "not found" and noindex, and the
    // storefront dropped out of Google's index until the next crawl.
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch brand",
      { status: error.response?.status || 0 }
    );
  }
};

export const fetchBrandById = createAsyncThunk("brands/fetchBrandById", getBrandRow);

/**
 * The open storefront's brand again, QUIETLY: `isFetching` is untouched, so
 * the page does not drop to its skeleton, and a failure leaves what is on
 * screen. For socket events that change which locations the storefront may
 * offer (an outlet switched on or off, 4 Oct 2026). Skipped unless that brand
 * is the one open.
 */
export const refreshSelectedBrand = createAsyncThunk(
  "brands/refreshSelectedBrand",
  getBrandRow,
  {
    condition: (arg, { getState }) => {
      const { brandId } = brandArg(arg);
      return (
        Boolean(brandId) &&
        String(getState().brands?.selectedBrand?._id || "") === String(brandId)
      );
    },
  }
);

export const fetchLatestFeaturedImages = createAsyncThunk(
  "brands/fetchLatestFeaturedImages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await publicRequest.get("/api/brands/latest-all");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch featured images"
      );
    }
  }
);

export const fetchLast5FeaturedImages = createAsyncThunk(
  "brands/fetchLast5FeaturedImages",
  async (brandId, { rejectWithValue }) => {
    try {
      const response = await publicRequest.get(`/api/brands/${brandId}/featured-image/history`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch featured image history"
      );
    }
  }
);

const initialState = {
  brands: [],
  featuredBrands: [],
  selectedBrand: null,
  featuredImages: [],
  featuredImagesFetching: false,
  latestFeaturedImages: [],
  featuredBrandsActive: true,
  isFetching: false,
  error: null,
  // True only when the API answered 404 for the brand read: the brand does
  // not exist. A failed or unanswered read leaves it false.
  brandNotFound: false,
  // The request id of the latest brand read; an older answer is dropped.
  brandReadId: null,
};

const brandSlice = createSlice({
  name: "brands",
  initialState,
  reducers: {
    setSelectedBrand: (state, action) => {
      state.selectedBrand = action.payload;
    },
    clearSelectedBrand: (state) => {
      state.selectedBrand = null;
      state.featuredImages = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    setFeaturedBrandsActive: (state, action) => {
      if (typeof action.payload === "boolean") {
        state.featuredBrandsActive = action.payload;
      }
    },
    /* Realtime `section:orderChanged`: an operator dragged this brand's
       products into a new order on the dashboard. The payload is the
       complete order the server wrote, so reorder in place — the products
       it names first, in that order, then anything it did not name (a
       product the server filtered for this viewer, or one added since) in
       their current order. No refetch, no flicker. Ignored for any brand
       other than the one on screen. */
    applyBrandSectionOrder: (state, action) => {
      const { brandId, sectionId, productIds } = action.payload || {};
      const brand = state.selectedBrand;
      if (!brand || !sectionId || !Array.isArray(productIds)) return;
      if (brandId && String(brand._id) !== String(brandId)) return;
      const sections = Array.isArray(brand.sections) ? brand.sections : [];
      const idx = sections.findIndex((s) => String(s._id) === String(sectionId));
      if (idx < 0) return;
      const products = Array.isArray(sections[idx].products)
        ? sections[idx].products
        : [];
      const byId = new Map(products.map((p) => [String(p._id), p]));
      const listed = new Set(productIds.map(String));
      sections[idx].products = [
        ...productIds.map((pid) => byId.get(String(pid))).filter(Boolean),
        ...products.filter((p) => !listed.has(String(p._id))),
      ];
    },
    /* Realtime `branchProduct:changed` (4 Oct 2026): a branch switched one
       product on or off, or sold it out. Patched in place on the open
       storefront: the badge at the branch it was read for, and `notAt`
       for "Get it from". No price travels on the event; a price change is
       picked up by the cart check. Ignored for any other brand. */
    applyBranchProductChange: (state, action) => {
      const brand = state.selectedBrand;
      const payload = action.payload || {};
      if (!brand || !branchEventIsFor(payload, [brand._id, brand.owner])) return;
      applyBranchEventToSections(brand.sections, payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all brands
      .addCase(fetchBrands.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.isFetching = false;
        const payload = action.payload || {};
        state.brands = payload.data || [];
        if (typeof payload.featuredBrandsActive === "boolean") {
          state.featuredBrandsActive = payload.featuredBrandsActive;
        }
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      // Fetch featured brands
      .addCase(fetchFeaturedBrands.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchFeaturedBrands.fulfilled, (state, action) => {
        state.isFetching = false;
        const payload = action.payload || {};
        state.featuredBrands = payload.data || [];
        if (typeof payload.featuredBrandsActive === "boolean") {
          state.featuredBrandsActive = payload.featuredBrandsActive;
        }
      })
      .addCase(fetchFeaturedBrands.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      // Fetch brand by ID. The LATEST read wins (4 Oct 2026): a shopper who
      // switches branch while the first read is in flight must never be
      // left on the catalogue of the branch they just left.
      .addCase(fetchBrandById.pending, (state, action) => {
        state.isFetching = true;
        state.error = null;
        state.brandNotFound = false;
        state.brandReadId = action.meta?.requestId || null;
      })
      .addCase(fetchBrandById.fulfilled, (state, action) => {
        if (state.brandReadId && action.meta?.requestId !== state.brandReadId) return;
        state.isFetching = false;
        state.selectedBrand = action.payload;
        state.brandNotFound = !action.payload;
      })
      .addCase(fetchBrandById.rejected, (state, action) => {
        if (state.brandReadId && action.meta?.requestId !== state.brandReadId) return;
        state.isFetching = false;
        state.error = action.payload;
        state.brandNotFound = action.meta?.status === 404;
      })
      // Replace only while the shopper is still on that brand.
      .addCase(refreshSelectedBrand.pending, (state, action) => {
        state.brandReadId = action.meta?.requestId || null;
      })
      .addCase(refreshSelectedBrand.fulfilled, (state, action) => {
        if (state.brandReadId && action.meta?.requestId !== state.brandReadId) return;
        const fresh = action.payload;
        if (
          fresh &&
          state.selectedBrand &&
          String(state.selectedBrand._id) === String(fresh._id)
        ) {
          state.selectedBrand = fresh;
        }
      })
      // Fetch latest featured images for all
      .addCase(fetchLatestFeaturedImages.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchLatestFeaturedImages.fulfilled, (state, action) => {
        state.isFetching = false;
        state.latestFeaturedImages = action.payload;
      })
      .addCase(fetchLatestFeaturedImages.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
      })
      // Fetch last 5 featured images
      .addCase(fetchLast5FeaturedImages.pending, (state) => {
        state.featuredImagesFetching = true;
        state.featuredImages = [];
        state.error = null;
      })
      .addCase(fetchLast5FeaturedImages.fulfilled, (state, action) => {
        state.featuredImagesFetching = false;
        state.featuredImages = action.payload || [];
      })
      .addCase(fetchLast5FeaturedImages.rejected, (state, action) => {
        state.featuredImagesFetching = false;
        state.featuredImages = [];
        state.error = action.payload;
      })
      .addCase(fetchFeaturedBrandsToggle.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchFeaturedBrandsToggle.fulfilled, (state, action) => {
        if (typeof action.payload?.featuredBrandsActive === "boolean") {
          state.featuredBrandsActive = action.payload.featuredBrandsActive;
        }
      })
      .addCase(fetchFeaturedBrandsToggle.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedBrand,
  clearSelectedBrand,
  clearError,
  setFeaturedBrandsActive,
  applyBrandSectionOrder,
  applyBranchProductChange,
} = brandSlice.actions;

export default brandSlice.reducer;
